import { useEffect, useState, useMemo } from 'react'
import { Box, Card, Grid, Toolbar, IconButton, List, Dialog, Fab, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Button } from '@mui/material'
import { Close, ArrowBackIos } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { styleModal3D, useStylesTheme } from '../../config'
import { MVAvatar } from '../../containers'
import { LoadingPage } from '../loading'
import { useAuth, useMachineContext, useMachines3DContext, useUser, useSitesContext } from '../../context'
import { segundosHoras } from '../../utils/NumberUtils'

const MachinesListPage = ({ route }) => {
    const { userData } = useAuth()
    const { admin, isSuperAdmin, isSapExecutive, isChiefMachinery, isShiftManager, isUniversalInspector, isUniversalChief } = useUser()
    const { sitesToSelection, siteSelected, setSiteSelected } = useSitesContext()
    const {
        machinesBySite,
        setMachineSelected,
        fetchMachinesByModel,
        machinesByModel,
        fetchMachineDetailsAndReports,
        machinesOfProject,
        machinesListSearchTerm,
        setMachinesListSearchTerm
    } = useMachineContext()
    const { todoArchivo3DListo } = useMachines3DContext()
    const [routeData, setRouteData] = useState('')
    const [open, setOpen] = useState(false)
    const [showMachinesList, setShowMachinesList] = useState(false)
    const [machine, setMachine] = useState()

    const navigate = useNavigate()

    const { id } = useParams()

    const initialMachine = useMemo(() => {
        try {
            return JSON.parse(id)
        } catch (e) {
            return {}
        }
    }, [id])

    const getMachineImageInfo = (m) => {
        if (!m) return { src: '../assets/no-image.webp', isMop: false }
        if (m.urlImagen) return { src: m.urlImagen, isMop: false }
        if (m.image) return { src: m.image, isMop: false }

        const mop = (machinesOfProject || []).find(p =>
            (p.modelo === (m.modelo || m.model) || p.model === (m.modelo || m.model)) &&
            (!p.brand || !m.brand || p.brand.toLowerCase() === m.brand.toLowerCase())
        )
        if (mop?.urlImagen) return { src: mop.urlImagen, isMop: true }
        if (mop?.image) return { src: mop.image, isMop: true }

        if (initialMachine?.urlImagen) return { src: initialMachine.urlImagen, isMop: true }
        if (initialMachine?.image) return { src: initialMachine.image, isMop: true }

        return { src: '../assets/no-image.webp', isMop: false }
    }

    const searchTerm = machinesListSearchTerm !== null && machinesListSearchTerm !== undefined
        ? machinesListSearchTerm
        : (initialMachine.model || initialMachine.modelo || '')

    const handleSearchTermChange = (newTerm) => {
        setMachinesListSearchTerm(newTerm)
    }

    const openModal3D = () => {
        if (todoArchivo3DListo) {
            setOpen(true)
        } else {
            alert('Espere al término de la descarga de todos los modelos 3D')
        }
    }

    useEffect(() => {
        setMachine(JSON.parse(id))
    }, [id])

    useEffect(() => {
        if (route === 'inspection') {
            setRouteData('Inspección')
        } else if (route === 'maintenance') {
            setRouteData('Mantención')
        } else if (route === 'machines') {
            setRouteData('Máquinas')
        }
    }, [route])

    // useEffect(() => {
    //     if (machine) {
    //         fetchMachinesByModel(JSON.parse(id).model)
    //     }
    // }, [machine])

    const isExecutive = admin || isSuperAdmin || isSapExecutive || isChiefMachinery || isShiftManager || isUniversalInspector || isUniversalChief

    // Filter available sites based on role permissions
    const availableSites = useMemo(() => {
        if (isExecutive) {
            return sitesToSelection.filter(s => s._id !== 'universal')
        } else {
            return sitesToSelection.filter(s => s._id !== 'all' && s._id !== 'universal')
        }
    }, [sitesToSelection, isExecutive])

    // Auto-select first site for non-executives if siteSelected is 'all', 'universal', or null
    useEffect(() => {
        if (!isExecutive && availableSites.length > 0) {
            if (!siteSelected || siteSelected._id === 'all' || siteSelected._id === 'universal') {
                setSiteSelected(availableSites[0])
            }
        }
    }, [availableSites, siteSelected, isExecutive, setSiteSelected])

    useEffect(() => {
        if (machinesBySite) {
            setShowMachinesList(true)
        }
    }, [machinesBySite])

    // Filter and sort display list
    const displayList = useMemo(() => {
        if (!machinesBySite) return []
        if (siteSelected?._id === 'universal') return []

        const isAllSites = !siteSelected || siteSelected.idObra === 'none' || siteSelected._id === 'all' || siteSelected._id === 'universal'

        // Pre-filter machinesBySite by user's assigned obras if not admin/universal and isAllSites is true
        let siteFilteredMachines = machinesBySite
        if (isAllSites && !admin && !isSuperAdmin && !isUniversalInspector && !isUniversalChief && userData?.obras) {
            const userObras = userData.obras.map(o => typeof o === 'object' && o !== null ? (o.idObra || o.idobra || o._id || o.id) : o).filter(Boolean).map(String)
            siteFilteredMachines = machinesBySite.filter(m => userObras.includes(String(m.idobra)) || userObras.includes(String(m.obra)))
        }

        const filtered = siteFilteredMachines.filter(m => {
            const currentIdObra = (typeof m.obra === 'object' && m.obra?.idObra) || m.idobra
            const matchSite = isAllSites || currentIdObra === siteSelected.idObra || m.idobra === siteSelected.idObra || m.obra === siteSelected._id
            if (!matchSite) return false

            if (!searchTerm) return true

            const term = searchTerm.toLowerCase()
            return (
                (m.brand && m.brand.toLowerCase().includes(term)) ||
                (m.modelo && m.modelo.toLowerCase().includes(term)) ||
                (m.model && m.model.toLowerCase().includes(term)) ||
                (m.equ && m.equ.toLowerCase().includes(term)) ||
                (m.equid && m.equid.toLowerCase().includes(term))
            )
        })

        return filtered.sort((a, b) => { return a.equ - b.equ })
    }, [machinesBySite, siteSelected, searchTerm, admin, isSuperAdmin, isUniversalInspector, isUniversalChief, userData])

    const goToMachineDetail = (machineData) => {
        setMachineSelected(machineData)
        navigate(`machine-detail/${machineData.equid}`)
    }

    const closeModal = () => {
        setOpen(false)
    }

    return (
        <Box height='100%'>
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <Card elevation={0} className={'pageCard'}>
                        {machine && <Grid container alignItems='center' justifyContent='center'>
                            <div style={{ width: '100%', textAlign: 'center', padding: 10 }}>
                                <div style={{ width: '100%', textAlign: 'center', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, paddingRight: 10, backgroundColor: '#F9F9F9', borderRadius: 10, display: 'flex', flexWrap: 'wrap', gap: 10, height: 'auto', minHeight: 56, padding: '10px' }}>
                                        <Box display="flex" alignItems="center">
                                            <IconButton onClick={() => setTimeout(() => {
                                                navigate(-1)
                                            }, 500)}>
                                                <ArrowBackIos style={{ color: '#333', fontSize: 16 }}/>
                                            </IconButton>
                                            <p style={{ marginTop: 0, marginBottom: 0, fontSize: 16, fontWeight: 'bold', marginRight: 15 }}>
                                                {machine && `${routeData}/${machine.type}`}
                                            </p>
                                        </Box>

                                        {/* Selector de Sitio / Obra */}
                                        <FormControl size="small" style={{ minWidth: 200, marginLeft: 'auto' }}>
                                            <InputLabel id="site-select-label">Sitio / Obra</InputLabel>
                                            <Select
                                                labelId="site-select-label"
                                                value={siteSelected && siteSelected._id !== 'universal' ? siteSelected._id : ''}
                                                onChange={(e) => {
                                                    const siteObj = availableSites.find(s => s._id === e.target.value)
                                                    if (siteObj) setSiteSelected(siteObj)
                                                }}
                                                label="Sitio / Obra"
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccione Obra</em>
                                                </MenuItem>
                                                {availableSites.map((s, i) => (
                                                    <MenuItem key={i} value={s._id}>
                                                        {s.idObra && s.idObra !== 'none' && s.idObra !== 'universal' ? `${s.idObra} - ${s.descripcion}` : s.descripcion}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Input de Búsqueda de Marca o Modelo */}
                                        <TextField
                                            size="small"
                                            placeholder="Buscar marca o modelo..."
                                            value={searchTerm}
                                            onChange={(e) => handleSearchTermChange(e.target.value)}
                                            style={{ minWidth: 200 }}
                                        />

                                        <Button onClick={() => handleSearchTermChange('')} style={{ minWidth: 100, backgroundColor: '#be2e26', color: '#fff', fontWeight: 'bold' }} variant="contained">
                                            Limpiar
                                        </Button>
                                        
                                        {/* Indicador de Equipos en Sitio */}
                                        <Box display="flex" alignItems="center" style={{ backgroundColor: '#be2e26', color: '#fff', padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 'bold' }}>
                                            Equipos en Sitio: {displayList.length}
                                        </Box>
                                    </Toolbar>
                                </div>
                            </div>
                            <div style={{ width: '100%', paddingLeft: 20, height: 40, textAlign: 'center' }}>
                                {
                                    (machine.modelo === '793-F' || machine.modelo === 'PC5500') &&
                                    <button style={{ cursor: 'pointer', height: 30, borderRadius: 20, position: 'relative', right: 10, paddingLeft: 30, paddingRight: 30 }} onClick={() => openModal3D()}>
                                        <strong>{`Ver modelo 3D de ${machine.type} ${machine.brand} ${machine.model}`}</strong>
                                    </button>}
                            </div>
                            <List style={{ width: '100vw', marginRight: 11, overflowY: 'scroll', maxHeight: '70vh', paddingLeft: 20, paddingRight: 20 }}>
                                {
                                    !showMachinesList &&
                                <LoadingPage />
                                }
                                {
                                    showMachinesList && displayList.map((machine, i) => {
                                        if (admin) {
                                            return (
                                                <Grid key={i} container style={{ minHeight: 148, marginBottom: 20, padding: 20, borderStyle: 'solid', borderWidth: 2, borderColor: '#CCC', borderRadius: 20, backgroundColor: machine.state ? 'white' : '#f7f7f7' }}>
                                                    <Grid item xs={12} sm={12} md={3} lg={2}>
                                                        {(() => {
                                                            const imgInfo = getMachineImageInfo(machine)
                                                            return <img src={imgInfo.src} style={{ height: 120, width: '100%', objectFit: 'cover', opacity: imgInfo.isMop ? 0.5 : 1, transition: 'opacity 0.2s' }} />
                                                        })()}
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={3} lg={2}>
                                                        <div style={{ padding: 10 }}>
                                                            <h2 style={{ margin: 0, fontSize: '100%' }}> <strong>{machine.type} {machine.equ}</strong> </h2>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={3} lg={3}>
                                                        {!machine.state && <div style={{ padding: 10, width: '100%' }}>
                                                            <p style={{ marginTop: 5, marginBottom: 5, color: 'red', fontWeight: 'bold' }}>Máquina deshabilitada</p>
                                                        </div>}
                                                        <div style={{ padding: 10 }}>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Marca: {machine.brand.toUpperCase()}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Modelo: {machine.model}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Código: {machine.equid}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Obra: {(typeof machine.obra === 'object' && machine.obra?.idObra) || machine.idobra}</p>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={3} lg={3}>
                                                        <div style={{ padding: 10 }}>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Horómetro Actual: {segundosHoras(machine.hourMeter)} hrs</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Número Interno: {machine.equ}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>Serie motor: {machine.enginesnr}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>SPM Inspección: {machine.idpminspeccion}</p>
                                                            <p style={{ marginTop: 5, marginBottom: 5 }}>SPM Mantención: {machine.idpmmantencion}</p>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={12} lg={2}>
                                                        <div style={{ width: '100%', textAlign: 'center' }} >
                                                            <button style={{ width: 100, height: 30, borderRadius: 20, marginLeft: 6 }} onClick={() => goToMachineDetail(machine)} /* component={Link} to={`/${route}/${JSON.stringify(machine)}` */ >
                                                                <strong>Ver más</strong>
                                                            </button>
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            )
                                        } else {
                                            if (machine.state) {
                                                return (
                                                    <Grid key={i} container style={{ minHeight: 148, marginBottom: 20, padding: 20, borderStyle: 'solid', borderWidth: 2, borderColor: '#CCC', borderRadius: 20 }}>
                                                        <Grid item xs={12} sm={12} md={3} lg={2}>
                                                            {(() => {
                                                                const imgInfo = getMachineImageInfo(machine)
                                                                return <img src={imgInfo.src} style={{ height: 120, width: '100%', objectFit: 'cover', opacity: imgInfo.isMop ? 0.5 : 1, transition: 'opacity 0.2s' }} />
                                                            })()}
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={3} lg={2}>
                                                            <div style={{ padding: 10 }}>
                                                                 <h2 style={{ margin: 0, fontSize: '100%' }}> <strong>{machine.type} {machine.equ}</strong> </h2>
                                                            </div>
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={3} lg={3}>
                                                            <div style={{ padding: 10 }}>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Marca: {machine.brand.toUpperCase()}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Modelo: {machine.model}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Código: {machine.equid}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Obra: {(typeof machine.obra === 'object' && machine.obra?.idObra) || machine.idobra}</p>
                                                            </div>
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={3} lg={3}>
                                                            <div style={{ padding: 10 }}>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Horómetro Actual: {segundosHoras(machine.hourMeter)} hrs</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Número Interno: {machine.equ}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>Serie motor: {machine.enginesnr}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>SPM Inspección: {machine.idpminspeccion}</p>
                                                                <p style={{ marginTop: 5, marginBottom: 5 }}>SPM Mantención: {machine.idpmmantencion}</p>
                                                            </div>
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={12} lg={2}>
                                                            <div style={{ width: '100%', textAlign: 'center' }} >
                                                                <button style={{ width: 100, height: 30, borderRadius: 20, marginLeft: 6 }} onClick={() => goToMachineDetail(machine)} /* component={Link} to={`/${route}/${JSON.stringify(machine)}` */ >
                                                                    <strong>Ver más</strong>
                                                                </button>
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            }
                                            return null
                                        }
                                    })
                                }
                                {
                                    showMachinesList && (!siteSelected || siteSelected._id === 'universal') && (
                                        <Grid container style={{ minHeight: 148, marginBottom: 20, padding: 20, borderStyle: 'solid', borderWidth: 2, borderColor: '#CCC', borderRadius: 20 }}>
                                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                                <p style={{ fontWeight: 'bold', textAlign: 'center' }}>Debe seleccionar una obra para ver las máquinas.</p>
                                            </Grid>
                                        </Grid>
                                    )
                                }
                                {
                                    showMachinesList && siteSelected && siteSelected._id !== 'universal' && displayList.length === 0 && (
                                        <Grid container style={{ minHeight: 148, marginBottom: 20, padding: 20, borderStyle: 'solid', borderWidth: 2, borderColor: '#CCC', borderRadius: 20 }}>
                                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                                <p style={{ fontWeight: 'bold', textAlign: 'center' }}>No hay máquinas asignadas a esta obra.</p>
                                            </Grid>
                                        </Grid>
                                    )
                                }
                            </List>
                            <div>
                                <Dialog
                                    open={open}
                                    fullScreen
                                >
                                    <MVAvatar machine={machine}/>
                                    <Fab onClick={() => closeModal()} style={{ position: 'absolute', right: 10, top: 10, boxShadow: 'none', backgroundColor: 'transparent' }} color="primary">
                                        <Close />
                                    </Fab>
                                </Dialog>
                            </div>
                        </Grid>}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

export default MachinesListPage
